/**
 * Entrare, registrarsi, rientrare.
 *
 * Una schermata sola con cinque stati, perché sono cinque momenti dello
 * stesso gesto — «voglio entrare» — e cambiare pagina a ogni passo fa perdere
 * il filo: si entra, ci si registra, si chiede una password nuova, si legge
 * «controlla la posta», si sceglie la password dal link. Il link di conferma
 * dell'email non ha nemmeno bisogno di un modulo: si apre e si entra.
 *
 * A sinistra il modulo e basta. A destra, su schermo largo, tre scene del
 * prodotto vero — una proposta, gli avvisi, il conto — che scorrono da sole:
 * chi arriva per la prima volta capisce cosa fa Otto guardandolo, non leggendo
 * un elenco di promesse.
 */

import { useEffect, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Eye,
  EyeOff,
  Lock,
  MailCheck,
} from "lucide-react";
import { api, ApiError } from "../lib/api.ts";
import { messaggioDi } from "../lib/errori.ts";
import { Pill, cx } from "../ui/primitives.tsx";
import { AnteprimaProdotto } from "./AnteprimaProdotto.tsx";

/** Lunghezza minima: la stessa del server (`PASSWORD_MIN`). */
const PASSWORD_MIN = 12;

type Stato =
  | { vista: "entra" }
  | { vista: "registrati" }
  | { vista: "dimenticata" }
  | { vista: "posta"; email: string; perche: "verifica" | "password" }
  | { vista: "nuova-password"; biglietto: string }
  | { vista: "verifica"; biglietto: string }
  | { vista: "pagato" }
  | { vista: "pagamento-fallito"; perche: "spento" | "stripe" };

/** I biglietti arrivano nell'indirizzo: `/?verifica=…` o `/?nuova-password=…`. */
export function bigliettoDallIndirizzo(): Stato | null {
  const parametri = new URLSearchParams(window.location.search);
  const verifica = parametri.get("verifica");
  if (verifica) return { vista: "verifica", biglietto: verifica };
  const nuova = parametri.get("nuova-password");
  if (nuova) return { vista: "nuova-password", biglietto: nuova };
  return null;
}

/**
 * Il ritorno dal pagamento: `/?pagato=…` da Stripe, `/?pagamento=…` quando la
 * pagina di Stripe non si è aperta. Non è un biglietto: chi ha già una
 * sessione aperta entra nell'app come sempre, invece di finire qui.
 */
function ritornoDalPagamento(): Stato | null {
  const parametri = new URLSearchParams(window.location.search);
  if (parametri.has("pagato")) return { vista: "pagato" };
  const fallito = parametri.get("pagamento");
  if (fallito === "spento" || fallito === "stripe")
    return { vista: "pagamento-fallito", perche: fallito };
  return null;
}

/** Il biglietto si usa una volta: tolto dall'indirizzo, non resta in cronologia. */
function pulisciIndirizzo() {
  window.history.replaceState(null, "", window.location.pathname);
}

export function Accesso({ onEntrato }: { onEntrato: () => void }) {
  const [stato, setStato] = useState<Stato>(
    () =>
      bigliettoDallIndirizzo() ?? ritornoDalPagamento() ?? { vista: "entra" },
  );

  // L'id della sessione di Stripe non serve a niente qui (l'account lo crea
  // il webhook): si toglie dall'indirizzo, in un effetto e non mentre si
  // legge, perché React la lettura può farla due volte.
  useEffect(() => {
    if (stato.vista === "pagato" || stato.vista === "pagamento-fallito")
      pulisciIndirizzo();
  }, [stato.vista]);

  const entrato = () => {
    pulisciIndirizzo();
    onEntrato();
  };

  return (
    <div className="flex h-full overflow-hidden bg-surface">
      {/* ── Il modulo ─────────────────────────────────────────────────── */}
      <div className="scroll-soft flex min-w-0 flex-1 flex-col overflow-y-auto px-6 py-8 sm:px-12 lg:max-w-[46%] lg:basis-[46%] lg:px-16 xl:px-24">
        <div className="flex items-center gap-2">
          <Logo />
          <span className="text-[19px] font-extrabold tracking-tight text-ink">
            Otto
          </span>
        </div>

        <div className="my-auto w-full max-w-[440px] py-12">
          <div key={stato.vista} className="animate-rise">
            {stato.vista === "entra" ? (
              <Entra onEntrato={entrato} onVai={setStato} />
            ) : stato.vista === "registrati" ? (
              <Registrati onVai={setStato} onEntrato={entrato} />
            ) : stato.vista === "dimenticata" ? (
              <Dimenticata onVai={setStato} />
            ) : stato.vista === "posta" ? (
              <ControllaPosta stato={stato} onVai={setStato} />
            ) : stato.vista === "pagato" ? (
              <Pagato onVai={setStato} />
            ) : stato.vista === "pagamento-fallito" ? (
              <PagamentoFallito perche={stato.perche} onVai={setStato} />
            ) : stato.vista === "nuova-password" ? (
              <NuovaPassword
                biglietto={stato.biglietto}
                onEntrato={entrato}
                onVai={(s) => {
                  pulisciIndirizzo();
                  setStato(s);
                }}
              />
            ) : (
              <Verifica
                biglietto={stato.biglietto}
                onEntrato={entrato}
                onVai={(s) => {
                  pulisciIndirizzo();
                  setStato(s);
                }}
              />
            )}
          </div>
        </div>

        <p className="text-[12px] text-ink-faint">
          Otto non modifica niente su Guesty senza il tuo ok. ·{" "}
          <a
            href="https://ottoguest.com/privacy"
            target="_blank"
            rel="noreferrer"
            className="underline-offset-2 hover:text-ink hover:underline"
          >
            Privacy
          </a>{" "}
          ·{" "}
          <a
            href="https://ottoguest.com/termini"
            target="_blank"
            rel="noreferrer"
            className="underline-offset-2 hover:text-ink hover:underline"
          >
            Termini
          </a>
        </p>
      </div>

      {/* ── Il prodotto, a destra ─────────────────────────────────────── */}
      <div className="hidden min-w-0 flex-1 p-3 lg:flex">
        <AnteprimaProdotto />
      </div>
    </div>
  );
}

// ── Entrare ──────────────────────────────────────────────────────────────────

function Entra({
  onEntrato,
  onVai,
}: {
  onEntrato: () => void;
  onVai: (s: Stato) => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [daVerificare, setDaVerificare] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const [devLogin, setDevLogin] = useState(false);
  // Aperta finché il server non dice il contrario: in sviluppo lo è sempre, e
  // un bottone che sparisce dopo mezzo secondo è meglio di uno che compare.
  const [registrazione, setRegistrazione] = useState<{
    aperta: boolean;
    landing: string | null;
  }>({ aperta: true, landing: null });

  // La scorciatoia di sviluppo si mostra solo se esiste davvero dall'altra
  // parte: in produzione il bottone non deve nemmeno comparire.
  useEffect(() => {
    void api
      .health()
      .then((h) => {
        setDevLogin(h.devLogin === true);
        setRegistrazione({
          aperta: h.registrazioneAperta !== false,
          landing: h.landing ?? null,
        });
      })
      .catch(() => setDevLogin(false));
  }, []);

  const entra = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrore(null);
    setDaVerificare(false);
    setInCorso(true);
    try {
      await api.login(email.trim(), password);
      onEntrato();
    } catch (caught) {
      if (
        caught instanceof ApiError &&
        caught.code === "email_non_verificata"
      ) {
        setDaVerificare(true);
      } else {
        setErrore(messaggioDi(caught));
        setPassword("");
      }
    } finally {
      setInCorso(false);
    }
  };

  const rimanda = async () => {
    setInCorso(true);
    try {
      await api.rimandaVerifica(email.trim());
      onVai({ vista: "posta", email: email.trim(), perche: "verifica" });
    } catch (caught) {
      setErrore(messaggioDi(caught));
    } finally {
      setInCorso(false);
    }
  };

  const senzaPassword = async () => {
    setErrore(null);
    setInCorso(true);
    try {
      await api.devLogin();
      onEntrato();
    } catch (caught) {
      setErrore(messaggioDi(caught));
    } finally {
      setInCorso(false);
    }
  };

  return (
    <>
      <Titolo
        titolo="Bentornato"
        sotto={
          <>
            Non hai un account?{" "}
            {registrazione.aperta ? (
              <button
                type="button"
                onClick={() => onVai({ vista: "registrati" })}
                className="font-bold text-ink underline-offset-2 hover:underline"
              >
                Registrati
              </button>
            ) : registrazione.landing ? (
              // Chiusa: si entra dopo aver scelto un abbonamento, dalla
              // landing. «Registrati» porterebbe a un «su invito».
              <a
                href={registrazione.landing}
                className="font-bold text-ink underline-offset-2 hover:underline"
              >
                Scopri Otto
              </a>
            ) : (
              <span className="font-bold text-ink">
                Scrivici e te lo apriamo
              </span>
            )}
          </>
        }
      />

      <form onSubmit={entra} className="mt-7 flex flex-col gap-3.5">
        <Campo
          id="email"
          placeholder="nome@esempio.it"
          label="Email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={setEmail}
          autoFocus
        />
        <CampoPassword
          id="password"
          label="Password"
          autoComplete="current-password"
          value={password}
          onChange={setPassword}
          azione={
            <button
              type="button"
              onClick={() => onVai({ vista: "dimenticata" })}
              className="text-[12.5px] font-semibold text-ink-soft underline-offset-2 hover:text-ink hover:underline"
            >
              Non la ricordi?
            </button>
          }
        />

        {daVerificare ? (
          <Avviso tono="warn">
            Devi ancora confermare l'email: apri il link che ti abbiamo mandato.{" "}
            <button
              type="button"
              onClick={rimanda}
              className="font-bold underline underline-offset-2"
            >
              Mandamelo di nuovo
            </button>
          </Avviso>
        ) : null}
        {errore ? <Avviso tono="danger">{errore}</Avviso> : null}

        <Pill
          variant="primary"
          size="lg"
          block
          type="submit"
          loading={inCorso}
          icon={<ArrowRight size={16} />}
          className="mt-1"
        >
          Entra
        </Pill>
      </form>

      {devLogin ? (
        <div className="mt-8 rounded-card border border-dashed border-line-strong px-4 py-3">
          <p className="text-[12px] leading-relaxed text-ink-faint">
            Server di sviluppo: puoi entrare come primo utente senza password.
            In produzione questo riquadro non esiste.
          </p>
          <Pill
            variant="ghost"
            size="sm"
            onClick={senzaPassword}
            disabled={inCorso}
            className="mt-1.5 -ml-2"
          >
            Entra senza password
          </Pill>
        </div>
      ) : null}
    </>
  );
}

// ── Registrarsi ──────────────────────────────────────────────────────────────

function Registrati({
  onVai,
  onEntrato,
}: {
  onVai: (s: Stato) => void;
  onEntrato: () => void;
}) {
  const [nome, setNome] = useState("");
  const [attivita, setAttivita] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const troppoCorta = password.length > 0 && password.length < PASSWORD_MIN;

  const registrati = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password.length < PASSWORD_MIN) {
      setErrore(`La password deve avere almeno ${PASSWORD_MIN} caratteri.`);
      return;
    }
    setErrore(null);
    setInCorso(true);
    try {
      const esito = await api.registrati({
        nome: nome.trim(),
        nomeAttivita: attivita.trim(),
        email: email.trim(),
        password,
      });
      if (esito.entrato) onEntrato();
      else onVai({ vista: "posta", email: esito.email, perche: "verifica" });
    } catch (caught) {
      setErrore(messaggioDi(caught));
    } finally {
      setInCorso(false);
    }
  };

  return (
    <>
      <Indietro onClick={() => onVai({ vista: "entra" })} />
      <Titolo
        titolo="Crea il tuo account"
        sotto={
          <>
            Hai già un account?{" "}
            <button
              type="button"
              onClick={() => onVai({ vista: "entra" })}
              className="font-bold text-ink underline-offset-2 hover:underline"
            >
              Entra
            </button>
          </>
        }
      />

      <form onSubmit={registrati} className="mt-7 flex flex-col gap-3.5">
        <div className="grid gap-3.5 sm:grid-cols-2">
          <Campo
            id="nome"
            placeholder="Marta"
            label="Il tuo nome"
            autoComplete="given-name"
            value={nome}
            onChange={setNome}
            autoFocus
          />
          <Campo
            id="attivita"
            label="Nome dell'attività"
            autoComplete="organization"
            placeholder="es. Case sul Lago"
            value={attivita}
            onChange={setAttivita}
          />
        </div>
        <Campo
          id="email-registrazione"
          placeholder="nome@esempio.it"
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={setEmail}
        />
        <CampoPassword
          id="password-registrazione"
          label="Password"
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
        />
        <Robustezza password={password} />

        {errore ? <Avviso tono="danger">{errore}</Avviso> : null}

        <Pill
          variant="primary"
          size="lg"
          block
          type="submit"
          loading={inCorso}
          disabled={troppoCorta}
          icon={<ArrowRight size={16} />}
          className="mt-1"
        >
          Crea l'account
        </Pill>
      </form>

      <p className="mt-5 text-[12.5px] leading-relaxed text-ink-faint">
        Poi colleghi Guesty: due chiavi, un minuto.
      </p>
    </>
  );
}

// ── Password dimenticata ─────────────────────────────────────────────────────

function Dimenticata({ onVai }: { onVai: (s: Stato) => void }) {
  const [email, setEmail] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const chiedi = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrore(null);
    setInCorso(true);
    try {
      await api.passwordDimenticata(email.trim());
      onVai({ vista: "posta", email: email.trim(), perche: "password" });
    } catch (caught) {
      setErrore(messaggioDi(caught));
    } finally {
      setInCorso(false);
    }
  };

  return (
    <>
      <Indietro onClick={() => onVai({ vista: "entra" })} />
      <Titolo
        titolo="Nuova password"
        sotto="Scrivi la tua email: ti mandiamo un link per sceglierne una nuova."
      />
      <form onSubmit={chiedi} className="mt-7 flex flex-col gap-3.5">
        <Campo
          id="email-dimenticata"
          placeholder="nome@esempio.it"
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={setEmail}
          autoFocus
        />
        {errore ? <Avviso tono="danger">{errore}</Avviso> : null}
        <Pill
          variant="primary"
          size="lg"
          block
          type="submit"
          loading={inCorso}
          icon={<ArrowRight size={16} />}
        >
          Mandami il link
        </Pill>
      </form>
    </>
  );
}

// ── Controlla la posta ───────────────────────────────────────────────────────

/**
 * Appena pagato. L'account lo crea il webhook di Stripe, che può arrivare un
 * attimo prima o un attimo dopo: qui non si promette che sia già pronto, si
 * dice dove arriverà il link. Chi aveva già un account entra come sempre.
 */
function Pagato({ onVai }: { onVai: (s: Stato) => void }) {
  return (
    <>
      <div className="grid size-14 place-items-center rounded-card-lg bg-ok-soft text-ok">
        <MailCheck size={26} />
      </div>
      <Titolo
        titolo="Pagamento ricevuto"
        sotto="Grazie. Ti stiamo mandando un'email con il link per scegliere la password: arriva all'indirizzo che hai usato per pagare, di solito entro un minuto."
      />

      <div className="mt-7 flex flex-col gap-2.5 rounded-card border border-line bg-surface-muted px-4 py-3.5 text-[13px] leading-relaxed text-ink-soft">
        <p>Non la trovi? Guarda nello spam.</p>
        <p>
          Avevi già un account? L'abbonamento è di nuovo attivo: entra con la
          tua solita password.
        </p>
      </div>

      <Pill
        variant="secondary"
        size="md"
        block
        onClick={() => onVai({ vista: "entra" })}
        icon={<ArrowRight size={15} />}
        className="mt-5"
      >
        Vai all'accesso
      </Pill>
    </>
  );
}

/** La pagina di Stripe non si è aperta: niente è stato addebitato. */
function PagamentoFallito({
  perche,
  onVai,
}: {
  perche: "spento" | "stripe";
  onVai: (s: Stato) => void;
}) {
  return (
    <>
      <Titolo
        titolo={
          perche === "spento"
            ? "I pagamenti non sono ancora attivi"
            : "Il pagamento non si è aperto"
        }
        sotto={
          perche === "spento"
            ? "Scrivici a ciao@ottoguest.com e ti apriamo l'account noi."
            : "Non ti abbiamo addebitato niente. Riprova fra qualche minuto, o scrivici a ciao@ottoguest.com."
        }
      />
      <Pill
        variant="secondary"
        size="md"
        block
        onClick={() => onVai({ vista: "entra" })}
        icon={<ArrowLeft size={15} />}
        className="mt-7"
      >
        Hai già un account? Entra
      </Pill>
    </>
  );
}

function ControllaPosta({
  stato,
  onVai,
}: {
  stato: Extract<Stato, { vista: "posta" }>;
  onVai: (s: Stato) => void;
}) {
  const [rimandata, setRimandata] = useState(false);
  const [inCorso, setInCorso] = useState(false);

  const rimanda = async () => {
    setInCorso(true);
    try {
      if (stato.perche === "verifica") await api.rimandaVerifica(stato.email);
      else await api.passwordDimenticata(stato.email);
      setRimandata(true);
    } finally {
      setInCorso(false);
    }
  };

  return (
    <>
      <div className="grid size-14 place-items-center rounded-card-lg bg-surface-muted text-ink">
        <MailCheck size={26} />
      </div>
      <Titolo
        titolo="Controlla la posta"
        sotto={
          stato.perche === "verifica"
            ? `Abbiamo mandato un link a ${stato.email}. Aprilo per confermare l'email.`
            : `Se ${stato.email} ha un account, ti è arrivato un link. Vale un'ora.`
        }
      />

      <div className="mt-7 flex flex-col gap-2.5 rounded-card border border-line bg-surface-muted px-4 py-3.5 text-[13px] leading-relaxed text-ink-soft">
        <p>Non è arrivata? Controlla nello spam.</p>
        {rimandata ? (
          <p className="font-semibold text-ok">Mandata di nuovo.</p>
        ) : (
          <button
            type="button"
            onClick={rimanda}
            disabled={inCorso}
            className="self-start font-bold text-ink underline underline-offset-2 disabled:opacity-50"
          >
            Mandamela di nuovo
          </button>
        )}
      </div>

      <Pill
        variant="secondary"
        size="md"
        block
        onClick={() => onVai({ vista: "entra" })}
        icon={<ArrowLeft size={15} />}
        className="mt-5"
      >
        Torna all'accesso
      </Pill>
    </>
  );
}

// ── Dal link: la password nuova ──────────────────────────────────────────────

function NuovaPassword({
  biglietto,
  onEntrato,
  onVai,
}: {
  biglietto: string;
  onEntrato: () => void;
  onVai: (s: Stato) => void;
}) {
  const [password, setPassword] = useState("");
  const [conferma, setConferma] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const diverse = conferma.length > 0 && conferma !== password;

  const salva = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password.length < PASSWORD_MIN) {
      setErrore(`La password deve avere almeno ${PASSWORD_MIN} caratteri.`);
      return;
    }
    if (password !== conferma) {
      setErrore("Le due password non coincidono.");
      return;
    }
    setErrore(null);
    setInCorso(true);
    try {
      await api.nuovaPassword(biglietto, password);
      onEntrato();
    } catch (caught) {
      setErrore(messaggioDi(caught));
    } finally {
      setInCorso(false);
    }
  };

  return (
    <>
      <Titolo
        titolo="Scegli la nuova password"
        sotto="Da ora entri con questa. Sugli altri dispositivi dovrai rientrare."
      />
      <form onSubmit={salva} className="mt-7 flex flex-col gap-3.5">
        <CampoPassword
          id="nuova"
          label="Nuova password"
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          autoFocus
        />
        <Robustezza password={password} />
        <CampoPassword
          id="conferma"
          label="Ripetila"
          autoComplete="new-password"
          value={conferma}
          onChange={setConferma}
        />
        {diverse ? (
          <p className="text-[12.5px] text-danger">
            Non coincide con la prima.
          </p>
        ) : null}
        {errore ? <Avviso tono="danger">{errore}</Avviso> : null}
        <Pill
          variant="primary"
          size="lg"
          block
          type="submit"
          loading={inCorso}
          icon={<Lock size={15} />}
        >
          Salva ed entra
        </Pill>
      </form>
      <button
        type="button"
        onClick={() => onVai({ vista: "dimenticata" })}
        className="mt-5 text-[13px] font-semibold text-ink-soft underline-offset-2 hover:underline"
      >
        Il link è scaduto? Chiedine uno nuovo
      </button>
    </>
  );
}

// ── Dal link: la conferma dell'email ─────────────────────────────────────────

/**
 * Una richiesta sola per biglietto, anche se il componente si monta due volte.
 *
 * In sviluppo React esegue gli effetti due volte: la prima chiamata usava il
 * biglietto, la seconda lo trovava già usato, e l'host leggeva «link non
 * valido» su un link che aveva appena funzionato. Il biglietto vale una volta,
 * e la richiesta deve partire una volta.
 */
const verifiche = new Map<string, Promise<void>>();
function verificaUnaVolta(biglietto: string): Promise<void> {
  let promessa = verifiche.get(biglietto);
  if (!promessa) {
    promessa = api.verificaEmail(biglietto);
    verifiche.set(biglietto, promessa);
  }
  return promessa;
}

function Verifica({
  biglietto,
  onEntrato,
  onVai,
}: {
  biglietto: string;
  onEntrato: () => void;
  onVai: (s: Stato) => void;
}) {
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    let annullato = false;
    verificaUnaVolta(biglietto)
      .then(() => {
        if (!annullato) onEntrato();
      })
      .catch((caught: unknown) => {
        if (!annullato) setErrore(messaggioDi(caught));
      });
    return () => {
      annullato = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biglietto]);

  if (errore) {
    return (
      <>
        <Titolo titolo="Link non valido" sotto={errore} />
        <Pill
          variant="primary"
          size="lg"
          block
          onClick={() => onVai({ vista: "entra" })}
          icon={<ArrowRight size={16} />}
          className="mt-7"
        >
          Vai all'accesso
        </Pill>
      </>
    );
  }

  return (
    <div className="flex flex-col items-center py-10 text-center">
      <span className="size-9 animate-spin rounded-full border-[3px] border-line border-t-ink" />
      <p className="mt-5 text-[15px] font-bold text-ink">
        Confermo la tua email…
      </p>
    </div>
  );
}

// ── Pezzi ───────────────────────────────────────────────────────────────────

function Titolo({ titolo, sotto }: { titolo: string; sotto: React.ReactNode }) {
  return (
    <div className="mt-5 first:mt-0">
      <h1 className="text-[36px] font-extrabold leading-tight tracking-tight text-ink">
        {titolo}
      </h1>
      <p className="mt-2 text-[14.5px] leading-relaxed text-ink-soft">
        {sotto}
      </p>
    </div>
  );
}

function Indietro({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="-ml-1 mb-5 flex items-center gap-1.5 rounded-field px-1 py-0.5 text-[13px] font-semibold text-ink-soft transition-colors duration-quick hover:text-ink"
    >
      <ArrowLeft size={15} />
      Indietro
    </button>
  );
}

function Avviso({
  tono,
  children,
}: {
  tono: "danger" | "warn";
  children: React.ReactNode;
}) {
  return (
    <p
      role="alert"
      className={cx(
        "animate-rise rounded-card px-3.5 py-2.5 text-[13px] font-semibold leading-snug",
        tono === "danger"
          ? "bg-danger-soft text-danger"
          : "bg-warn-soft text-warn",
      )}
    >
      {children}
    </p>
  );
}

export function Campo({
  id,
  label,
  icona,
  value,
  onChange,
  azione,
  ...rest
}: {
  id: string;
  label: string;
  icona?: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  azione?: React.ReactNode;
} & Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "id"
>) {
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-[12.5px] font-bold text-ink">
          {label}
        </label>
        {azione}
      </div>
      <div
        className={cx(
          "flex items-center gap-2.5 rounded-field border border-line-strong/70 bg-surface px-3.5 shadow-[0_1px_2px_rgb(0_0_0/0.04)]",
          "transition-all duration-quick",
          "focus-within:border-ink focus-within:ring-2 focus-within:ring-ink/10",
        )}
      >
        {icona ? (
          <span className="shrink-0 text-ink-faint">{icona}</span>
        ) : null}
        <input
          id={id}
          required
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full bg-transparent py-3 text-[14.5px] text-ink outline-none placeholder:text-ink-faint"
          {...rest}
        />
      </div>
    </div>
  );
}

export function CampoPassword(
  props: Omit<Parameters<typeof Campo>[0], "icona" | "type">,
) {
  const [visibile, setVisibile] = useState(false);
  return (
    <div className="relative">
      <Campo
        {...props}
        type={visibile ? "text" : "password"}
        className="w-full bg-transparent py-3 pr-8 text-[14.5px] text-ink outline-none placeholder:text-ink-faint"
      />
      <button
        type="button"
        onClick={() => setVisibile((v) => !v)}
        aria-label={visibile ? "Nascondi la password" : "Mostra la password"}
        className="absolute bottom-[11px] right-3 grid size-6 place-items-center rounded-field text-ink-faint transition-colors duration-quick hover:text-ink"
      >
        {visibile ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
}

/**
 * Quanto è robusta, detto con la lunghezza: è l'unica cosa che conta davvero
 * (vedi `PASSWORD_MIN` sul server). Niente regole su maiuscole e simboli.
 */
export function Robustezza({ password }: { password: string }) {
  if (password.length === 0) {
    return (
      <p className="-mt-1.5 text-[12px] text-ink-faint">
        Almeno {PASSWORD_MIN} caratteri. Una frase che ricordi vale più di un
        simbolo in mezzo a una parola corta.
      </p>
    );
  }
  const livello =
    password.length < PASSWORD_MIN ? 0 : password.length < 18 ? 1 : 2;
  const etichette = ["Troppo corta", "Va bene", "Ottima"];
  const colori = ["bg-danger", "bg-warn", "bg-ok"];
  const testo = ["text-danger", "text-warn", "text-ok"];
  return (
    <div className="-mt-1 flex items-center gap-2.5">
      <div className="flex flex-1 gap-1">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={cx(
              "h-1 flex-1 rounded-pill transition-colors duration-calm",
              i <= livello ? colori[livello] : "bg-surface-sunken",
            )}
          />
        ))}
      </div>
      <span className={cx("text-[12px] font-bold", testo[livello])}>
        {etichette[livello]}
        {livello === 0 ? ` · ${password.length}/${PASSWORD_MIN}` : ""}
      </span>
    </div>
  );
}

export function Logo({ chiaro }: { chiaro?: boolean }) {
  return (
    <svg viewBox="0 0 32 32" className="size-9 shrink-0" aria-hidden>
      <path
        d="M16 3.5 4.5 12.2v13.4c0 1.6 1.3 2.9 2.9 2.9h17.2c1.6 0 2.9-1.3 2.9-2.9V12.2L16 3.5Z"
        fill={chiaro ? "white" : "var(--color-accent)"}
      />
      <path
        d="M12.2 17.4h7.6M12.2 21.2h4.8"
        stroke={chiaro ? "var(--color-nav)" : "white"}
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}
